<template>
  <b-overlay :show="loading" rounded="lg" class="files-page">
    <!-- Toolbar -->
    <b-card class="toolbar-card mb-4">
      <b-row class="align-items-center">
        <b-col cols="12" md="6" lg="4" class="mb-2 mb-md-0">
          <b-input-group>
            <b-input-group-prepend>
              <b-input-group-text><i class="fas fa-search"></i></b-input-group-text>
            </b-input-group-prepend>
            <b-form-input
                v-model="filters.keyword"
                placeholder="جستجو در فایل‌ها..."
                @keyup.enter="applyFilters"
            />
          </b-input-group>
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mb-2 mb-md-0">
          <b-row>
            <b-col cols="6">
              <b-form-select
                  v-model="filters.mediaType"
                  :options="mediaTypeOptions"
                  @change="applyFilters"
              />
            </b-col>
            <b-col cols="6">
              <b-form-select
                  v-model="filters.status"
                  :options="statusOptions"
                  @change="applyFilters"
              />
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="12" md="6" lg="4" class="d-flex justify-content-end">
          <b-button variant="primary" class="mr-2" @click="applyFilters">
            <i class="fas fa-filter"></i> اعمال
          </b-button>
          <b-button variant="outline-secondary" @click="clearFilters">
            <i class="fas fa-times"></i> پاک
          </b-button>
          <!-- Selection info when selectable -->
          <span v-if="selectable && selectedIds.length > 0" class="ml-2 text-muted">
            {{ selectedIds.length }} انتخاب شده
          </span>
        </b-col>
      </b-row>
    </b-card>

    <!-- File Grid -->
    <b-row v-if="files.length > 0" class="file-grid">
      <b-col
          v-for="file in files"
          :key="file.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="3"
          class="mb-4"
      >
        <FileCard
            :file="file"
            :selectable="selectable"
            :selected="isSelected(file.id)"
            @select="toggleSelection(file.id)"
            @refetch="fetchFiles"
            @view="viewFile(file.id)"
            @edit="editMetadata(file)"
            @hls="openHlsConversion(file.id)"
            @delete="confirmDelete(file.id)"
        />
      </b-col>
    </b-row>
    <div v-else class="text-center py-5">
      <i class="fas fa-folder-open" style="font-size: 4rem; color: #cbd5e1;"></i>
      <h4 class="mt-3">هیچ فایلی یافت نشد</h4>
      <p class="text-muted">با استفاده از دکمه آپلود، فایل جدید اضافه کنید.</p>
    </div>

    <!-- Pagination -->
    <b-row v-if="totalCount > 0" class="mt-3">
      <b-col cols="12" class="d-flex justify-content-between align-items-center flex-wrap">
        <span class="text-muted">نمایش {{ files.length }} از {{ totalCount }} فایل</span>
        <b-pagination
            v-model="pageIndex"
            :total-rows="totalCount"
            :per-page="pageSize"
            :hide-goto-end-buttons="true"
            @change="onPageChange"
            size="md"
            class="mb-0"
        />
      </b-col>
    </b-row>

    <!-- Modals (controlled by $bvModal) -->
    <FileDetailModal
        :file="detailFile"
        @edit="editMetadata(detailFile)"
        @hls="openHlsConversion(detailFile.id)"
        @delete="confirmDelete(detailFile.id)"
        @updated="handleFileUpdated"
    />

    <EditMetadataModal
        :file="editFile"
        @save="saveMetadata"
    />

    <HlsConversionModal
        :file="hlsFile"
        @convert="startHlsConversion"
    />

    <DeleteConfirmModal
        @confirm="deleteFile"
    />
  </b-overlay>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupText,
  BOverlay,
  BPagination,
  BRow
} from 'bootstrap-vue'
import {
  MediaLibraryConvertToHlsRequest,
  MediaLibraryDeleteFileRequest,
  MediaLibraryGetFileRequest,
  MediaLibrarySearchRequest,
  MediaLibraryUpdateMetadataRequest
} from '@/libs/Api/MediaLibrary'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import DeleteConfirmModal from "@/views/components/Library/DeleteConfirmModal.vue"
import HlsConversionModal from "@/views/components/Library/HlsConversionModal.vue"
import EditMetadataModal from "@/views/components/Library/EditMetadataModal.vue"
import FileDetailModal from "@/views/components/Library/FileDetailModal.vue"
import FileCard from "@/views/components/Library/FileCard.vue"

export default {
  name: 'FilesList',
  components: {
    FileCard,
    FileDetailModal,
    EditMetadataModal,
    HlsConversionModal,
    DeleteConfirmModal,
    BOverlay,
    BCard,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupText,
    BFormInput,
    BFormSelect,
    BButton,
    BPagination
  },
  props: {
    // Selection mode props
    selectable: {
      type: Boolean,
      default: false
    },

    multiple: {
      type: Boolean,
      default: true
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      files: [],
      totalCount: 0,
      pageIndex: 1,
      pageSize: 20,
      filters: {
        keyword: '',
        mediaType: '',
        status: ''
      },
      mediaTypeOptions: [
        { value: '', text: 'همه انواع' },
        { value: '1', text: 'تصویر' },
        { value: '2', text: 'ویدیو' },
        { value: '3', text: 'صوتی' },
        { value: '4', text: 'سند' }
      ],
      statusOptions: [
        { value: '', text: 'همه وضعیت‌ها' },
        { value: '0', text: 'در انتظار' },
        { value: '1', text: 'در حال پردازش' },
        { value: '2', text: 'پردازش شده' },
        { value: '3', text: 'ناموفق' }
      ],
      detailFile: null,
      editFile: null,
      hlsFile: null,
      deleteFileId: null,
      // Internal selectedIds (v-model)
      selectedIds: [...this.value]
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.selectedIds = [...newVal]
        }
      }
    },
    selectedIds: {
      handler(newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  created() {
    this.fetchFiles()
  },
  methods: {
    // ===== API Calls =====
    fetchFiles() {
      this.loading = true
      const data = {
        Keyword: this.filters.keyword,
        MediaType: this.filters.mediaType,
        Status: this.filters.status,
        PageIndex: this.pageIndex - 1,
        PageSize: this.pageSize,
        SortBy: 'CreatedAt',
        SortDesc: true
      }
      const request = new MediaLibrarySearchRequest(this)
      request.setParams(data)
      request.fetch(
          (content) => {
            this.files = content.items || []
            this.totalCount = content.totalCount || 0
            this.loading = false
          },
          (error) => {
            this.loading = false
            this.showToast('error', 'خطا در بارگذاری فایل‌ها', error.message)
          }
      )
    },
    applyFilters() {
      this.pageIndex = 1
      this.fetchFiles()
    },
    clearFilters() {
      this.filters.keyword = ''
      this.filters.mediaType = ''
      this.filters.status = ''
      this.pageIndex = 1
      this.fetchFiles()
    },
    onPageChange(page) {
      this.pageIndex = page
      this.fetchFiles()
    },

    // ===== View File Detail =====
    viewFile(fileId) {
      this.loading = true
      const request = new MediaLibraryGetFileRequest(this)
      request.setParams(fileId)
      request.fetch(
          (content) => {
            this.detailFile = content
            this.$bvModal.show('modal-detail')
            this.loading = false
          },
          (error) => {
            this.loading = false
            this.showToast('error', 'خطا در دریافت جزئیات', error.message)
          }
      )
    },

    // ===== Edit Metadata =====
    editMetadata(file) {
      this.editFile = file
      this.$bvModal.show('modal-edit')
    },
    saveMetadata(updatedData) {
      this.loading = true
      const data = {
        title: updatedData.title,
        alt: updatedData.alt,
        description: updatedData.description,
        tags: updatedData.tags
      }
      const request = new MediaLibraryUpdateMetadataRequest(this)
      request.setParams({ fileId: this.editFile.id, ...data })
      request.fetch(
          () => {
            this.loading = false
            this.$bvModal.hide('modal-edit')
            this.showToast('success', 'موفق', 'متادیتا بروزرسانی شد')
            this.fetchFiles()
            if (this.detailFile && this.detailFile.id === this.editFile.id) {
              this.viewFile(this.editFile.id)
            }
          },
          (error) => {
            this.loading = false
            this.showToast('error', 'خطا در بروزرسانی', error.message)
          }
      )
    },

    // ===== Delete File =====
    confirmDelete(fileId) {
      this.deleteFileId = fileId
      this.$bvModal.show('modal-delete')
    },
    deleteFile() {
      this.loading = true
      const request = new MediaLibraryDeleteFileRequest(this)
      request.setParams({ fileId: this.deleteFileId })
      request.fetch(
          () => {
            this.loading = false
            this.$bvModal.hide('modal-delete')
            this.showToast('success', 'موفق', 'فایل حذف شد')
            this.fetchFiles()
            if (this.detailFile) this.detailFile = null
          },
          (error) => {
            this.loading = false
            this.showToast('error', 'خطا در حذف', error.message)
          }
      )
    },

    // ===== HLS Conversion =====
    openHlsConversion(fileId) {
      const file = this.files.find(f => f.id === fileId)
      if (!file) return
      this.hlsFile = file
      this.$bvModal.show('modal-hls')
    },
    startHlsConversion(qualities) {
      if (!qualities || !qualities.length) {
        this.showToast('warning', 'هشدار', 'حداقل یک کیفیت انتخاب کنید.')
        return
      }
      this.loading = true
      const data = { qualities }
      const request = new MediaLibraryConvertToHlsRequest(this)
      request.setParams({ fileId: this.hlsFile.id, ...data })
      request.fetch(
          () => {
            this.loading = false
            this.$bvModal.hide('modal-hls')
            this.showToast('success', 'موفق', 'تبدیل HLS شروع شد. وضعیت را در صفحه فایل‌ها بررسی کنید.')
            this.fetchFiles()
            this.viewFile(this.hlsFile.id)
          },
          (error) => {
            this.loading = false
            this.showToast('error', 'خطا در تبدیل', error.message)
          }
      )
    },

    // ===== Handle File Update =====
    handleFileUpdated() {
      this.fetchFiles()
      if (this.detailFile) {
        this.viewFile(this.detailFile.id)
        this.fetchFiles()
      }
    },

    // ===== Selection methods =====
    isSelected(fileId) {
      return this.selectedIds.includes(fileId)
    },
    toggleSelection(fileId) {
      if (!this.selectable) return
      let newSelection
      if (this.multiple) {
        if (this.isSelected(fileId)) {
          newSelection = this.selectedIds.filter(id => id !== fileId)
        } else {
          newSelection = [...this.selectedIds, fileId]
        }
      } else {
        // Single selection: if clicked file is already selected, deselect; else select only that one
        newSelection = this.isSelected(fileId) ? [] : [fileId]
      }
      this.selectedIds = newSelection
    },

    // ===== Toast Helper =====
    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        position: 'bottom-center',
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'XIcon',
          variant,
          text
        }
      })
    }
  }
}
</script>

<style scoped>
.files-page >>> .card-body {
  padding: 1.5rem;
}
.toolbar-card {
  border-radius: 18px;
  border: 1px solid #e2e8f0;
}
</style>
